import { Component, inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from "@angular/core";
import { SystemAlertService } from "../../services/api/system-alert/sys-alert.service";
import { PermissionService } from "../../services/api/permission/permission.service";
import { SvgIconComponent } from "../../shared/components/svg-icon/svg-icon.component";
import { SimplebarAngularModule } from "simplebar-angular";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { DialogService, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { SystemAlertComponent } from "./forms/system-alert/system-alert.component";
import { first } from "rxjs";
import { ConfirmationService, MessageService } from "@openng/optimus-ui/api";
import { HasPermissionDirective } from "../../shared/directives/has-permission/has-permission.directive";
import { SysAlert } from "../../models/api/sysAlert";

@Component({
  selector: "app-system-alerts",
  standalone: true,
  imports: [HasPermissionDirective, SvgIconComponent, SimplebarAngularModule, TranslateModule],
  templateUrl: "./system-alerts.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./system-alerts.component.scss",
})
export class SystemAlertsComponent implements OnInit {
  public permissions = inject(PermissionService);
  private systemAlertsService = inject(SystemAlertService);
  private translate = inject(TranslateService);
  private confirm = inject(ConfirmationService);
  private message = inject(MessageService);

  private dialog = inject(DialogService);
  private ref: DynamicDialogRef | null = null;
  public systemAlerts: WritableSignal<SysAlert[]> = signal([]);

  ngOnInit() {
    this.getSystemAlert();
  }

  /**
   * Opens the system alert form with an empty system alert.
   * When the form is submitted, a new system alert will be created.
   */
  public onSystemAlertAdd() {
    this.openSystemAlertDialog("add");
  }
  /**
   * Sends a delete request on confirmation, updates the list on success, and shows a message.
   * @param event - Triggering event.
   * @param systemAlert - Alert to delete.
   */
  public onSystemAlertDelete(event: Event, systemAlert: SysAlert) {
    this.confirm.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translate.instant("system_alerts.confirm_delete"),
      key: "global",
      accept: () => {
        this.systemAlertsService
          .deleteAlert(systemAlert)
          .pipe(first())
          .subscribe({
            next: () => {
              this.getSystemAlert();
              this.message.add({
                key: "global",
                severity: "success",
                detail: this.translate.instant("system_alerts.success_delete"),
                life: 4500,
              });
            },
          });
      },
    });
  }
  /**
   * Opens the system alert form with the given system alert.
   * When the form is submitted, the system alert will be updated.
   * @param systemAlert The system alert to be edited.
   */
  public onSystemAlertEdit(systemAlert: SysAlert) {
    this.openSystemAlertDialog("edit", systemAlert);
  }

  /**
   * Opens the system alert form with the given system alert and action type.
   * @param actionType "add" or "edit"
   * @param systemAlert The system alert to be edited (only for "edit" action type)
   * @returns The dialogRef, which can be used to close the dialog
   */
  private openSystemAlertDialog(actionType: "add" | "edit", systemAlert?: SysAlert) {
    this.ref = this.dialog.open(SystemAlertComponent, {
      data: {
        systemAlert: systemAlert,
        actionType: actionType,
      },
      header: this.translate.instant("system_alerts.form.title." + actionType),
      modal: true,
      width: "80vw",
      contentStyle: { overflow: "auto", padding: "20px 100px" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      closable: true,
    });
    this.ref?.onClose.subscribe((success: boolean) => {
      if (success) {
        this.getSystemAlert();
      }
    });
  }

  /**
   * Requests the list of inactive system alerts from the server and updates the local observable.
   */
  private getSystemAlert() {
    this.systemAlertsService
      .getAlerts(false)
      .pipe(first())
      .subscribe({
        next: (systemAlerts) => {
          this.systemAlerts.set(systemAlerts);
        },
      });
  }
}
